import { DimensionsMixin } from "@nrk/element-dimensions-mixin";
import { ReactiveElement, ReactiveState } from "@nrk/reactive-element";
import { PlayerActions } from "../player/player-actions.js";
import { isPlayerEvent, PlayerEvents } from "../player/player-events.js";
import { computeCuePosition } from "../utils/cue-position.js";
import { getElementOfType } from "../utils/dom.js";
class SubtitlesElement extends DimensionsMixin(ReactiveElement) {
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      subtitleState: ReactiveState,
      fullscreenState: Object,
      pipState: Object,
      airPlayState: Object
    };
  }
  /** Player aspect ratio */
  playerAspectRatio;
  /* Video aspect ratio */
  videoAspectRatio;
  /** The subtitle state object (shadowed player property) */
  subtitleState;
  /** Disable sending speak events for subtitle cues */
  disableSpokenSubtitles = false;
  /** The fullscreen state object (shadowed player property) */
  fullscreenState;
  /** The picture-in-picture state object (shadowed player property) */
  pipState;
  /** The AirPlay state object (shadowed player property) */
  airPlayState;
  /** Override to configure cue positioning */
  cueConfig = {};
  action = PlayerActions.Speak;
  player;
  value;
  activeCuePositions = [];
  _unshadowProperties;
  connectedCallback() {
    super.connectedCallback();
    this.player = getElementOfType(this);
    this.player.addEventListener(PlayerEvents.QualityChange, this);
    this._unshadowProperties = this.player.shadowProperties(["subtitleState", "fullscreenState"], this);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.player?.removeEventListener(PlayerEvents.QualityChange, this);
    this._unshadowProperties?.();
    this.player = null;
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (!this.isConnected || !this.player) {
      return;
    }
    if (changedProperties.has("subtitleState") && this.subtitleState) {
      const cues = this.subtitleState.activeCues;
      const stripTags = (s) => s.replace(/<[/\w][^<]*>/g, "");
      const text = cues.map((cue) => stripTags(cue.text)).join(" ");
      if (text && !this.disableSpokenSubtitles) {
        this.value = text;
        this.dispatchEvent(new Event("change", { cancelable: true, bubbles: true }));
      }
      this.activeCuePositions = cues.map((cue) => computeCuePosition(cue, this.cueConfig));
    }
    if (changedProperties.has("fullscreenState")) {
      if (this.player.activeSession?.canControlSubtitlesDisplay()) {
        const useNativeRendering = (this.fullscreenState?.isActive && this.fullscreenState.isNativeUI) ?? false;
        if (this.subtitleState?.nativeDisplayEnabled !== useNativeRendering) {
          this.player.activeSession?.displaySubtitles(useNativeRendering);
        }
      }
    }
    if (changedProperties.has("dimensions")) {
      this.playerAspectRatio = this.player.clientWidth / this.player.clientHeight;
      this.updateRenderingArea();
    }
  }
  handleEvent(event) {
    if (isPlayerEvent(event, PlayerEvents.QualityChange)) {
      if (!this.videoAspectRatio && this.player.activeSession?.getCurrentQuality()) {
        const quality = this.player.activeSession.getCurrentQuality();
        if (quality?.width && quality.height) {
          this.playerAspectRatio = this.player.clientWidth / this.player.clientHeight;
          this.videoAspectRatio = quality.width / quality.height;
          this.updateRenderingArea();
        }
      }
    }
  }
  /**
   * Check this before rendering HTML elements. Don't render the subtitles
   * when:
   * - The subtitles are being rendered natively.
   * - The video is not displayed inside the player.
   */
  isRenderingEnabled() {
    return !this.subtitleState?.nativeDisplayEnabled && !this.pipState?.isActive && !this.airPlayState?.isActive;
  }
  /**
   * Make this element cover the visible area of the video, so that the
   * positioning of the subtitle cues don't need to worry about the size or
   * shape of the player.
   */
  updateRenderingArea() {
    const blackBars = this.getBlackBars();
    this.style.top = blackBars.top ?? "0px";
    this.style.right = blackBars.right ?? "0px";
    this.style.bottom = blackBars.bottom ?? "0px";
    this.style.left = blackBars.left ?? "0px";
  }
  /**
   * Calculate the "black bars" between the player and the video. Black bars in
   * the video itself are not accounted for.
   */
  getBlackBars() {
    const playerAspectRatio = this.playerAspectRatio;
    const videoAspectRatio = this.videoAspectRatio;
    if (playerAspectRatio === void 0 || videoAspectRatio === void 0) {
      return {};
    }
    if (playerAspectRatio === videoAspectRatio || this.getObjectFit() !== "contain") {
      return {};
    }
    if (playerAspectRatio > videoAspectRatio) {
      const blackBarSize = (1 - videoAspectRatio / playerAspectRatio) / 2 * 100;
      return {
        right: `${blackBarSize}%`,
        left: `${blackBarSize}%`
      };
    } else {
      const blackBarSize = (1 - playerAspectRatio / videoAspectRatio) / 2 * 100;
      return {
        top: `${blackBarSize}%`,
        bottom: `${blackBarSize}%`
      };
    }
  }
  getObjectFit() {
    const video = this.player?.querySelector("video");
    return video ? getComputedStyle(video).objectFit : "contain";
  }
}
export {
  SubtitlesElement
};
